/**
 * Customer marketplace service availability — single source for Home UI + booking gates.
 * Backend mirrors these defaults in nemacareBE/config/careServiceAvailability.js.
 */

export const CARE_SERVICE_STATUS = {
  LIVE: "live",
  COMING_SOON: "coming_soon",
};

/** Default availability when Ops has not overridden marketplace types. */
export const DEFAULT_CARE_TYPE_AVAILABILITY = {
  attendant: {
    status: CARE_SERVICE_STATUS.LIVE,
    bookingEnabled: true,
    statusLabel: "LIVE",
  },
  nurses: {
    status: CARE_SERVICE_STATUS.LIVE,
    bookingEnabled: true,
    statusLabel: "LIVE",
  },
  physio: {
    status: CARE_SERVICE_STATUS.LIVE,
    bookingEnabled: true,
    statusLabel: "LIVE",
  },
};

/** Drop stored pre-launch overrides so LIVE defaults apply after nurse/physio launch. */
export function omitLegacyComingSoonOverrides(careTypeAvailability) {
  if (!careTypeAvailability || typeof careTypeAvailability !== "object") {
    return careTypeAvailability;
  }
  const next = { ...careTypeAvailability };
  for (const key of ["nurses", "physio"]) {
    const row = next[key];
    if (!row || typeof row !== "object") continue;
    const status = String(row.status || "").toLowerCase();
    const label = String(row.statusLabel || "").toUpperCase();
    if (
      status === CARE_SERVICE_STATUS.COMING_SOON &&
      row.bookingEnabled === false &&
      label === "COMING SOON"
    ) {
      delete next[key];
    }
  }
  return next;
}

export function normalizeCareTypeKey(value) {
  const raw = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (!raw) return "attendant";
  if (raw.includes("nurse")) return "nurses";
  if (raw.includes("physio") || raw.includes("therapist")) return "physio";
  if (raw.includes("attendant") || raw.includes("care")) return "attendant";
  return raw;
}

/**
 * Merge platform config overrides (optional careTypeAvailability map) with defaults.
 * Disabled care types stay visible with Coming Soon when listed in enabledCareTypes.
 */
export function resolveCareTypeAvailability(marketplaceTypes = null) {
  const src =
    marketplaceTypes?.careTypeAvailability && typeof marketplaceTypes.careTypeAvailability === "object"
      ? omitLegacyComingSoonOverrides(marketplaceTypes.careTypeAvailability)
      : {};
  const out = {};
  for (const key of Object.keys(DEFAULT_CARE_TYPE_AVAILABILITY)) {
    const defaults = DEFAULT_CARE_TYPE_AVAILABILITY[key];
    const override = src[key] && typeof src[key] === "object" ? src[key] : {};
    const status = String(override.status || defaults.status).toLowerCase();
    const bookingEnabled =
      typeof override.bookingEnabled === "boolean"
        ? override.bookingEnabled
        : status === CARE_SERVICE_STATUS.LIVE;
    out[key] = {
      status: status === CARE_SERVICE_STATUS.LIVE ? CARE_SERVICE_STATUS.LIVE : CARE_SERVICE_STATUS.COMING_SOON,
      bookingEnabled,
      statusLabel:
        status === CARE_SERVICE_STATUS.LIVE
          ? "LIVE"
          : String(override.statusLabel || defaults.statusLabel || "COMING SOON").toUpperCase(),
    };
  }
  return out;
}

export function getCareTypeAvailability(careType, marketplaceTypes = null) {
  const key = normalizeCareTypeKey(careType);
  const map = resolveCareTypeAvailability(marketplaceTypes);
  return map[key] || DEFAULT_CARE_TYPE_AVAILABILITY.attendant;
}

export function isCareTypeBookingEnabled(careType, marketplaceTypes = null) {
  return Boolean(getCareTypeAvailability(careType, marketplaceTypes)?.bookingEnabled);
}
